import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { User, PostAuthLoginBody, PostAuthLogin200, AuthMeResponse, TimesheetReport } from '../../api/generated/model';
import { postAuthLogin, postAuthLogout, getAuthMe } from '../../api/generated/arrayscapeGamingDashboardAPI';

interface AuthState {
  user: User | null;
  realUser: User | null;
  isImpersonating: boolean;
  timesheetReports: TimesheetReport[];
  token: string | null;
  isAuthenticated: boolean;
  loading: boolean;
  error: string | null;
}

const initialState: AuthState = {
  user: null,
  realUser: null,
  isImpersonating: false,
  timesheetReports: [],
  token: localStorage.getItem('token'),
  isAuthenticated: false,
  loading: false,
  error: null,
};

export const login = createAsyncThunk(
  'auth/login',
  async (credentials: PostAuthLoginBody, { rejectWithValue }) => {
    try {
      const response = await postAuthLogin(credentials);
      if (response.access_token) localStorage.setItem('token', response.access_token);
      if (response.refresh_token) localStorage.setItem('refresh_token', response.refresh_token);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Login failed');
    }
  }
);

export const logout = createAsyncThunk('auth/logout', async (_, { dispatch }) => {
  try {
    await postAuthLogout();
  } catch (error) {
    console.error('Logout error:', error);
  } finally {
    localStorage.removeItem('token');
    localStorage.removeItem('refresh_token');
    // Clear the browser history by redirecting to login
    window.location.href = '/login';
  }
});

export const getCurrentUser = createAsyncThunk(
  'auth/getCurrentUser',
  async (_, { rejectWithValue }) => {
    try {
      const user = await getAuthMe();
      return user;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to get user');
    }
  }
);

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setError: (state, action: PayloadAction<string>) => {
      state.error = action.payload;
    },
    setCredentials: (state, action: PayloadAction<{ user: User; token: string }>) => {
      state.user = action.payload.user;
      state.token = action.payload.token;
      state.isAuthenticated = true;
    },
    setImpersonation: (state, action: PayloadAction<{ realUser: User | null; isImpersonating: boolean }>) => {
      state.realUser = action.payload.realUser ?? null;
      state.isImpersonating = action.payload.isImpersonating;
    },
    setTimesheetReports: (state, action: PayloadAction<TimesheetReport[]>) => {
      state.timesheetReports = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(login.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(login.fulfilled, (state, action: PayloadAction<PostAuthLogin200>) => {
        state.loading = false;
        state.user = action.payload.user || null;
        state.token = action.payload.access_token || null;
        state.isAuthenticated = true;
        state.error = null;
      })
      .addCase(login.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
        state.isAuthenticated = false;
      })
      .addCase(logout.fulfilled, (state) => {
        state.user = null;
        state.token = null;
        state.isAuthenticated = false;
        state.error = null;
      })
      .addCase(getCurrentUser.pending, (state) => {
        state.loading = true;
      })
      .addCase(getCurrentUser.fulfilled, (state, action: PayloadAction<AuthMeResponse>) => {
        state.loading = false;
        state.user = action.payload.user || null;
        state.realUser = action.payload.real_user ?? null;
        state.isImpersonating = !!action.payload.real_user;
        state.timesheetReports = action.payload.timesheet_reports ?? [];
        state.isAuthenticated = true;
      })
      .addCase(getCurrentUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
        state.isAuthenticated = false;
        state.token = null;
        localStorage.removeItem('token');
      });
  },
});

export const { clearError, setError, setCredentials, setImpersonation, setTimesheetReports } = authSlice.actions;
export default authSlice.reducer;